import {dateTimeCsv, roundValue} from '../render/format';
import type {CubeMeasure, StatsKey} from '../types';

export type TableValue = string | number | null;

/** plain: left-aligned text in a plain cell (label is a row header, bold); text: free text that wraps; date: an ISO 8601 wall-clock timestamp (shown localized, exported as `YYYY-MM-DD HH:mm`) */
export type ColumnKind = 'label' | 'rank' | 'value' | 'delta' | 'count' | 'bar' | 'text' | 'date' | 'plain';

export interface TableColumn {
    id: string;
    kind: ColumnKind;
    /** Raw text; the view escapes it */
    label: string;
    /** value/bar/delta: precision and unit. A delta without a measure is an integer (a rank change) */
    measure?: CubeMeasure;
    /** One of the five-number columns of a `stats` measure */
    stat?: StatsKey;
    /** Delta colouring: the sign that counts as an improvement; without it the delta stays neutral */
    good?: 1 | -1;
    /** Bar scale */
    range?: [number, number];
    /** Stays in view on horizontal scroll; only a leading run of columns can be */
    sticky?: boolean;
    /** delta: what it is the difference to (the builder computes the value; header title, later significance) */
    against?: 'previous' | 'reference';
    /** Header tooltip; wins over the one `against` gives */
    title?: string;
    /** Starts with a divider like a column group does, without being in one */
    divided?: boolean;
    /** Faint background tint on the header and the cells, to set the column apart */
    tinted?: boolean;
}

/** Contiguous columns under one shared header cell */
export interface TableColumnGroup {
    id: string;
    /** Raw text; the view escapes it */
    label: string;
    columns: string[];
}

/** group: a heading row between the data rows; sorting keeps it in place and sorts the rows under it */
export type RowRole = 'data' | 'total' | 'gap' | 'group';

export interface TableCell {
    /** Raw number (rounded only for display and CSV) or a label */
    value: TableValue;
    lowBase?: boolean;
    color?: string;
    title?: string;
    /** Overrides the column's measure where rows are the measures themselves */
    measure?: CubeMeasure;
    /** Reserved for significance against the column's `against`; not rendered yet */
    sig?: 'higher' | 'lower';
}

export interface TableRow {
    key: string;
    role: RowRole;
    /** By column id; gap and group rows have none */
    cells: Record<string, TableCell>;
    /** Group rows: the heading text */
    label?: string;
    highlight?: 'top' | 'bottom';
    /** Left out of a ranking (low base, missing member) */
    unranked?: boolean;
}

export type SortDirection = 'asc' | 'desc';

export interface TableSort {
    column: string;
    dir: SortDirection;
}

export interface TableSpec {
    columns: TableColumn[];
    /** Two-row header: grouped columns under a shared cell, the others span both rows */
    groups?: TableColumnGroup[];
    rows: TableRow[];
    /** The order the rows already come in, when it follows a column */
    sort?: TableSort;
    /** false: the headers are plain text, the viewer cannot reorder (rows come ordered and paged from the server) */
    sortable?: boolean;
}

export function cellMeasure(column: TableColumn, cell: TableCell | undefined): CubeMeasure | undefined {
    return cell?.measure ?? column.measure;
}

function hasUnit(column: TableColumn): boolean {
    return (column.kind === 'value' || column.kind === 'bar') && !!column.measure?.unit?.trim();
}

function csvValue(column: TableColumn, cell: TableCell | undefined): TableValue {
    const value = cell?.value ?? null;
    const measure = cellMeasure(column, cell);

    if (column.kind === 'date') {
        return typeof value === 'string' ? dateTimeCsv(value) : '';
    }

    return typeof value === 'number' && measure && column.kind !== 'count' && column.kind !== 'rank'
        ? roundValue(value, measure) ?? ''
        : value ?? '';
}

/** Group of each grouped column by column id */
export function columnGroups(table: TableSpec): Map<string, TableColumnGroup> {
    return new Map((table.groups ?? []).flatMap(group => group.columns.map(id => [id, group] as const)));
}

/**
 * Header + rows as `arrayToCsvDownload` wants them (empty cells as '', the exporter writes null as text); the locale
 * decimal separator is applied by the exporter. One header row keeps it machine-readable, so a grouped column carries
 * its group's label.
 */
export function tableToCsv(table: TableSpec): TableValue[][] {
    const groups = columnGroups(table);
    const header = table.columns.map(column => {
        const label = hasUnit(column) ? `${column.label} [${column.measure!.unit!.trim()}]` : column.label;
        const group = groups.get(column.id);

        // An ungrouped column says in its title what a short header leaves out (comparison's "B vs A" difference)
        return group ? `${group.label} – ${label}` : column.title ?? label;
    });
    const rows = table.rows
        .filter(row => row.role !== 'gap')
        .map(row => row.role === 'group'
            ? table.columns.map((_column, position) => position === 0 ? row.label ?? '' : '')
            : table.columns.map(column => csvValue(column, row.cells[column.id])));

    return [header, ...rows];
}
